import React, { useState, useRef, useEffect } from 'react';
import { Info, X, ChevronDown, Search, Menu, Trash2, ChevronsUpDown, CircleCheck } from 'lucide-react';
import { useHistory } from "react-router-dom";
import Tooltip from '../addentities/Tooltip.js';
import { ReactSortable } from 'react-sortablejs';
import { createPortal } from 'react-dom';
import WorkInProgress from '../../components/WorkInProgress.js';
import TestSyncModal from '../../components/TestSyncModal.js';
import CharacterLimitedTextarea from '../../components/CharacterLimitedTextarea';
import './style.css';
const portalRoot = typeof document !== 'undefined' ? document.getElementById('portal-root') : null;
const OptionPortal = ({ children, style }) => {
    if (portalRoot) {
        return createPortal(
            <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 2000 }}>
                {children}
            </div>,
            portalRoot
        );
    }

    return (
        <div className="options-container w-full bg-white border rounded shadow-lg" style={{ ...style, overflow: 'auto', zIndex: 2000 }}>
            {children}
        </div>
    );
};

export default function AddListing() {
    const history = useHistory();

    // Discard modal
    const [discard_modal_open, set_discard_modal_open] = useState(false);
    const discard_ref = useRef(null);
    const handle_discard = () => set_discard_modal_open(true);
    useEffect(() => {
        function handleClickOutside(event) {
            if (discard_ref.current && !discard_ref.current.contains(event.target)) {
                set_discard_modal_open(false);
            }
        }
        if (discard_modal_open) document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [discard_modal_open]);

    const breadcrumbs = ["Listing", "Platform Listing", "Add Listing"];

    // Platform mapping rows
    const [rows, set_rows] = useState([
        { id: 1, platform: '', product_id: '', sku_id: '', platform_open: false }
    ]);
    const [rowsUpdateCheck, setRowsUpdateCheck] = useState(0);
    const [enableTestSync, setEnableTestSync] = useState(false);
    const [showTestSyncModal, setShowTestSyncModal] = useState(false);
    const row_refs = useRef({});

    const add_row = () => {
        const newId = rows.length ? Math.max(...rows.map(r => r.id)) + 1 : 1;
        set_rows(prev => [...prev, { id: newId, platform: '', product_id: '', sku_id: '', platform_open: false }]);
    };
    const delete_row = id => set_rows(prev => prev.filter(r => r.id !== id));
    const update_row = (id, changes) => {
        set_rows(prev => prev.map(r => r.id === id ? { ...r, ...changes } : r))
        setRowsUpdateCheck(rowsUpdateCheck + 1);
    };

    const save_ref2 = useRef(null);
    const [save_modal_open_2, set_save_modal_open_2] = useState(false);
    const handle_save_confirm_2 = () => set_save_modal_open_2(false);
    useEffect(() => {
        function outside(e) { if (save_ref2.current && !save_ref2.current.contains(e.target)) set_save_modal_open_2(false); }
        if (save_modal_open_2) document.addEventListener('mousedown', outside);
        return () => document.removeEventListener('mousedown', outside);
    }, [save_modal_open_2]);

    const select_stock_ref = useRef(null);
    const [select_stock_open, set_select_stock_open] = useState(false);
    const [select_stock_search, set_select_stock_search] = useState("");
    const [form_data, set_form_data] = useState({
        select_stock: "",
        id: "",
        status: "1",
        notes: "",
    });

    const toggle_select_stock = () => set_select_stock_open((s) => !s);

    const STOCK_OPTIONS = [
        { title: "Samsung S25 Plus", sku: "Mint, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Samsung S25 Plus", sku: "Icyblue, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Samsung S25 Plus", sku: "Navy, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Samsung S25 Plus", sku: "Navy, 12GB, 256GB", condition: "Used", status: "Pristine" },
        { title: "Samsung S25 Plus", sku: "Navy, 12GB, 512GB", condition: "Used", status: "Pristine" },
        { title: "Samsung S25 Plus", sku: "Silver Shadow, 12GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Samsung S25 Plus", sku: "Blueblack, 12GB, 256GB", condition: "New", status: "Unactivated" },

        // Extra variations
        { title: "Apple iPhone 16", sku: "Starlight, 8GB, 256GB", condition: "New", status: "Activated" },
        { title: "Apple iPhone 16", sku: "Black, 8GB, 512GB", condition: "Used", status: "Refurbished" },
        { title: "Apple iPhone 15", sku: "Red, 6GB, 128GB", condition: "New", status: "Unactivated" },
        { title: "Apple iPad Air", sku: "Space Gray, 8GB, 256GB", condition: "New", status: "Unactivated" },
        { title: "Apple iPad Pro", sku: "Silver, 16GB, 1TB", condition: "Used", status: "Pristine" },
    ];

    useEffect(() => {
        function onDocClick(e) {
            if (select_stock_ref.current && !select_stock_ref.current.contains(e.target)) {
                set_select_stock_open(false);
            }
        }
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    const handle_select_stock_select = (opt) => {
        set_form_data((prev) => ({ ...prev, select_stock: opt }));
        set_select_stock_open(false);
        set_select_stock_search("");
    };

    // --- Utilities ---
    const escapeRegExp = (string) => String(string).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

    const normalizeForSearch = (s) =>
        String(s || "")
            .replace(/[\u2013\u2014–—\-|\/\\(),:;]+/g, " ")
            .replace(/\s+/g, " ")
            .trim()
            .toLowerCase();

    const buildTokens = (query) => normalizeForSearch(query).split(" ").filter(Boolean);

    // highlight for multiple tokens, but this helper will be used ONLY for title & sku
    const highlightForTokens = (text, tokens) => {
        if (!tokens || tokens.length === 0) return text;

        const uniqueTokens = Array.from(new Set(tokens)).sort((a, b) => b.length - a.length);
        const safePattern = uniqueTokens.map((t) => escapeRegExp(t)).join("|");
        const regex = new RegExp(`(${safePattern})`, "ig");

        return String(text).split(regex).map((part, i) => {
            if (!part) return null;
            if (regex.test(part)) {
                return (
                    <span key={i} style={{ color: "#0D6EFD" }}>
                        {part}
                    </span>
                );
            }
            return part;
        });
    };

    const tokens = buildTokens(select_stock_search);

    const filteredOptions = tokens.length
        ? STOCK_OPTIONS.filter((opt) => {
            const hay = normalizeForSearch(`${opt.title} ${opt.sku} ${opt.condition} ${opt.status}`);
            return tokens.every((tok) => hay.includes(tok));
        })
        : STOCK_OPTIONS;

    const showFinalBg = filteredOptions.length === 1 && tokens.length > 0;

    // Platform static options
    const PLATFORM_OPTIONS = ['Shopee SG', 'Lazada SG', 'Tik Tok SG'];

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (select_stock_open && select_stock_ref.current && !select_stock_ref.current.contains(e.target)) {
                set_select_stock_open(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [select_stock_open]);

    // Close any open platform portals when clicking outside (works with portalRoot)
    useEffect(() => {
        const handler = (e) => {
            const clickedInPortal = portalRoot && portalRoot.contains(e.target);
            set_rows(prev => prev.map(row => {
                const el = row_refs.current[row.id]?.current;
                if (!clickedInPortal && el && !el.contains(e.target)) {
                    return { ...row, platform_open: false };
                }
                return row;
            }));
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);
    const [showWip, setShowWip] = useState(false);

    useEffect(() => {
        // check to enable or disable the test sync button
        rows.forEach((r) => {
            if (r.platform && r.product_id && r.sku_id) {
                setEnableTestSync(true);
            }
        });
    }, [rowsUpdateCheck]);

    return (
        <div className="company-body main-company-body-inner new-look">
            <div className="breadcrumb">
                {breadcrumbs.map((crumb, index) => {
                    const isLast = index === breadcrumbs.length - 1;
                    return (
                        <span
                            key={index}
                            className={`breadcrumb-item ${crumb === "Listing & Pricing" ? "clickable" : ""}`}
                            onClick={() => { if (crumb === "Listing & Pricing") history.push('/listing-pricing'); }}
                            style={{ cursor: crumb === "Listing & Pricing" ? 'pointer' : 'default' }}
                        >
                            {crumb}{!isLast ? '  ' : ''}
                        </span>
                    );
                })}
            </div>

            <div className='edit-organisation-info'>
                <div className='edit-organisation-left'>
                    <h1>Add Listing <Tooltip content="Add Listing" title="Add Listing" body="Add Listing"><Info size={16} className="info-icon" /></Tooltip></h1>
                </div>
                <div className='discard-save-organisation-right d-flex align-items-center justify-content-end flex-wrap gap-2'>
                    <button className="dark-button" type="button" onClick={() => history.goBack()}> Cancel </button>

                    <TestSyncModal
                        isOpen={showTestSyncModal}
                        onClose={() => setShowTestSyncModal(false)}
                        onConfirm={() => setShowTestSyncModal(false)}
                        rows={rows}
                        setRows={set_rows}
                    />
                    <button className="dark-border" type="button" onClick={() => set_save_modal_open_2(true)}>Save & Exit</button>
                </div>
            </div>

            <div className="employee-tabs-container">
                {save_modal_open_2 && (
                    <>
                        <div className="drawer-overlay" />
                        <div className="entity-drawer modal-popup-cls" ref={save_ref2} style={{ maxWidth: 650, margin: '0 auto', background: 'white', borderRadius: 12, zIndex: 1050, padding: 24, position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', boxShadow: '0px 2px 10px rgba(0,0,0,0.2)', height: 'auto', right: 'auto', overflow: 'inherit' }}>
                            <div className="drawer-header d-flex justify-content-end align-items-center pt-0 pe-0">
                                <button className="close-btn" onClick={() => set_save_modal_open_2(false)}><X size={20} /></button>
                            </div>
                            <h3 style={{ textAlign: 'left', color: '#212529', fontSize: '20px', fontWeight: '600', lineHeight: '24px', fontFamily: 'Montserrat', marginBottom: '5px' }}>The edit function implementation</h3>
                            <div className="drawer-footer" style={{ paddingTop: '10px' }}>
                                <button className="btn-save" onClick={handle_save_confirm_2}>OK</button>
                            </div>
                        </div>
                    </>
                )}

                <div className="tab-content" id="edit-spu-content">
                    <div className="info-section">
                        {/* SPU / Stock INFO CARD */}
                        <div className="info-card new-info-overflow-auto">
                            <div className="info-header"><h4 className="info-title">Stock Info</h4></div>
                            <div className="info-content d-block">
                                <div className='grid-info-adding'>

                                    {/* ROW: Select Stock & ID */}
                                    <div className='row gappingcls-added'>
                                        <div className="col-lg-6 col-md-6 col-12">
                                            <div className="info-inner">
                                                <label htmlFor="select_stock" className="form-label d-flex align-items-center gap-1">
                                                    Select Stock<span className="error-color">*</span>
                                                    <Tooltip title="Select Stock" body="Select a Stock for Listing and Platform Mapping"><Info size={16} className="info-icon" /></Tooltip>
                                                </label>

                                                <div style={{ position: "relative" }} ref={select_stock_ref}>
                                                    <div
                                                        className="form-control common-input-all d-flex justify-between items-center cursor-pointer rounded p-2"
                                                        onClick={toggle_select_stock}
                                                        style={{ display: "flex", justifyContent: "space-between", alignItems: "center", minHeight: "40px" }}
                                                    >
                                                        {form_data.select_stock ? (
                                                            <div style={{ display: "flex", flexDirection: "column" }}>
                                                                <span style={{ fontSize: "14px", color: "#212529" }}>{form_data.select_stock.title}</span>

                                                                {/* SKU row (single line) */}
                                                                <div style={{ fontSize: "12px", color: "#6C757D", display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                                                                    <span style={{ display: "flex", alignItems: "center", gap: "4px", lineHeight: "normal" }}>
                                                                        <span>SKU:</span>{" "}
                                                                        <span>{form_data.select_stock.sku}</span>
                                                                    </span>

                                                                    <span style={{ margin: "0 6px" }}>|</span>

                                                                    <span
                                                                        style={{
                                                                            background: "#FFEB00",
                                                                            // background: form_data.select_stock.condition === "New" ? "#D1E7DD" : "#FFF3CD",
                                                                            // border: `1px solid ${form_data.select_stock.condition === "New" ? "#198754" : "#997404"}`,
                                                                            borderRadius: "100px",
                                                                            fontSize: "10px",
                                                                            fontWeight: 600,
                                                                            color: "#000000",
                                                                            // color: form_data.select_stock.condition === "New" ? "#198754" : "#997404",
                                                                            padding: "2px 5px",
                                                                            marginLeft: "4px",
                                                                            marginRight: "6px",
                                                                        }}
                                                                    >
                                                                        {form_data.select_stock.condition}
                                                                    </span>
                                                                    <span style={{
                                                                        // color: "#6C757D",
                                                                        background: "#FFEB00",
                                                                        borderRadius: "100px",
                                                                        fontSize: "10px",
                                                                        fontWeight: 600,
                                                                        color: "#000000",
                                                                        padding: "2px 5px",
                                                                    }}>{form_data.select_stock.status}</span>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            "Search and Select"
                                                        )}
                                                        <ChevronDown size={16} />
                                                    </div>

                                                    {select_stock_open && (
                                                        <div
                                                            className="z-10 w-full bg-white border rounded shadow-lg mt-1"
                                                            style={{ maxHeight: "320px", overflowY: "auto", position: "absolute", right: 0, left: 0, width: "100%", zIndex: 1 }}
                                                        >
                                                            {/* Search box */}
                                                            <div
                                                                className="search-box"
                                                                style={{
                                                                    display: "flex",
                                                                    alignItems: "center",
                                                                    minWidth: "260px",
                                                                    border: "1px solid rgb(108, 117, 125)",
                                                                    borderRadius: "8px",
                                                                    background: "#fff",
                                                                    position: "relative",
                                                                    margin: "0.5rem",
                                                                }}
                                                            >
                                                                <div style={{ position: "absolute", left: "10px", display: "flex", alignItems: "center", height: "100%" }}>
                                                                    <Search size={20} />
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    autoFocus
                                                                    style={{ width: "100%", padding: "10px 10px 10px 40px", border: "none", outline: "none", background: "transparent", fontSize: "14px" }}
                                                                    placeholder="Search stock..."
                                                                    value={select_stock_search}
                                                                    onChange={(e) => set_select_stock_search(e.target.value)}
                                                                />
                                                            </div>

                                                            {/* List items */}
                                                            {filteredOptions.map((opt, index) => {
                                                                const hay = normalizeForSearch(`${opt.title} ${opt.sku} ${opt.condition} ${opt.status}`);
                                                                const matches = tokens.length ? tokens.every((tok) => hay.includes(tok)) : true;
                                                                const bg = showFinalBg && matches ? "#FFFEF0" : "white";

                                                                return (
                                                                    <div
                                                                        key={index}
                                                                        className="p-2 flex flex-col cursor-pointer"
                                                                        onClick={() => handle_select_stock_select(opt)}
                                                                        style={{ background: bg }}
                                                                    >
                                                                        {/* Title */}
                                                                        <span style={{ fontSize: "14px", color: "#212529", display: "block" }}>
                                                                            {highlightForTokens(opt.title, tokens)}
                                                                        </span>

                                                                        {/* SKU row: kept inline (no wrap) and highlight only for SKU text */}
                                                                        <div style={{ fontSize: "12px", color: "#6C757D", display: "flex", alignItems: "center", whiteSpace: "nowrap" }}>
                                                                            <span style={{ display: "flex", alignItems: "center", gap: "4px", lineHeight: "normal" }}>
                                                                                <span>SKU:</span>{" "}
                                                                                <span>{highlightForTokens(opt.sku, tokens)}</span>
                                                                            </span>

                                                                            <span style={{ margin: "0 6px" }}>|</span>

                                                                            {/* condition pill - NO blue highlighting here */}
                                                                            <span
                                                                                style={{
                                                                                    background: "#FFEB00",
                                                                                    borderRadius: "100px",
                                                                                    fontSize: "10px",
                                                                                    fontWeight: 600,
                                                                                    color: "#000000",
                                                                                    padding: "2px 5px",
                                                                                    marginLeft: "4px",
                                                                                    marginRight: "6px",
                                                                                }}
                                                                            >
                                                                                {opt.condition}
                                                                            </span>

                                                                            <span style={{
                                                                                background: "#FFEB00",
                                                                                borderRadius: "100px",
                                                                                fontSize: "10px",
                                                                                fontWeight: 600,
                                                                                color: "#000000",
                                                                                padding: "2px 5px",
                                                                            }}>{opt.status}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}

                                                            {filteredOptions.length === 0 && (
                                                                <div style={{ padding: 12, color: "#6C757D", fontSize: 14 }}>No results found</div>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label htmlFor="status_active" className="form-label">Status <span className='error-color'>*</span></label>
                                                <div className='flexing-type-radio'>
                                                    <div className="form-check">
                                                        <input className="form-check-input" type="radio" id="status_active" name="status" value="1" checked={form_data.status === '1'} onChange={(e) => set_form_data({ ...form_data, status: e.target.value })} />
                                                        <label className="form-check-label" htmlFor="status_active" style={{ fontWeight: 'normal', fontSize: '14px' }}>Active</label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input className="form-check-input" type="radio" id="status_inactive" name="status" value="0" checked={form_data.status === '0'} onChange={(e) => set_form_data({ ...form_data, status: e.target.value })} />
                                                        <label className="form-check-label" htmlFor="status_inactive" style={{ fontWeight: 'normal', fontSize: '14px' }}>Inactive</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    {/* ROW: Status */}
                                    <div className='row gappingcls-added'>
                                        <div className='col-lg-6 col-md-6 col-12'>
                                            <div className="info-inner">
                                                <label htmlFor="id" className="form-label d-flex align-items-center gap-1">ID
                                                    {/* <Tooltip title="ID" body="ID"><Info size={16} className="info-icon" /></Tooltip> */}
                                                </label>
                                                <CharacterLimitedTextarea
                                                    id="id"
                                                    // placeholder="ID"
                                                    name="id"
                                                    value={form_data.id}
                                                    onChange={(e) => set_form_data({ ...form_data, id: e.target.value })}
                                                    maxLength={50}
                                                    counterPosition="end" // or "below" if you prefer below the input
                                                />
                                                {/* <input type="text" className="form-control common-input-all" id="id" name="id" placeholder="ID" value={form_data.id} onChange={(e) => set_form_data({ ...form_data, id: e.target.value })} /> */}
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* Platform mapping card (portal dropdown for platform selection) */}
                        <div className="info-card new-info-overflow-auto main-company-body-inner">
                            <div className="info-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <h4 className="info-title">Platform Mapping</h4>
                                <button className={!enableTestSync ? 'disabled' : ''} style={{ borderRadius: '8px', background: '#ffffff', padding: '10px', fontSize: '12px', border: "1px solid #212529", height: "34px", minWidth: "100px", display: "flex", justifyContent: "center", alignItems: "center", gap: '10px', fontFamily: "Montserrat", fontSize: '12px', fontWeight: "600", fontStyle: "normal", lineHeight: "normal" }} onClick={() => {
                                    if (enableTestSync) {
                                        setShowTestSyncModal(true);
                                    }
                                }}>Test Sync</button>
                            </div>
                            <div className="info-content d-block entities-tab">
                                <div style={{ padding: '20px', boxShadow: '0px 1px 4px #0000001A', borderRadius: '12px' }}>

                                    {/* {rows.length === 0 && show_missing_sku && (
                                        <div
                                            className="d-flex align-items-center justify-content-between"
                                            role="alert"
                                            style={{
                                                boxShadow: '0px 4px 10px 0px #0000001F',
                                                padding: '15px 10px',
                                                borderRadius: '12px',
                                                border: '1px solid #DC3545',
                                                backgroundColor: '#F8D7DA',
                                                color: '#58151C',
                                                fontSize: '12px',
                                                position: 'fixed',
                                                top: '10px',
                                                width: 'calc(100% - 20px)',
                                                maxWidth: '640px',
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                zIndex: 2000
                                            }}
                                        >
                                            <div className="d-flex align-items-center">
                                                <Info className="me-2" />
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <strong>Selected Stock is already listed</strong> —— Please choose a different stock
                                                </div>
                                            </div>

                                            <X
                                                size={16}
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => set_show_missing_sku(false)}
                                            />
                                        </div>
                                    )} */}


                                    <div className="table-wrapper" style={{ position: 'relative', overflowX: 'auto' }}>
                                        <table className="entities-table tab-table w-100">
                                            <thead height="35px">
                                                <tr>
                                                    <th />
                                                    <th><span>Platform <span style={{ color: "red" }}>*</span> </span></th>
                                                    <th><span>Product ID  <span style={{ color: "red" }}>*</span></span></th>
                                                    <th><span>SKU ID  <span style={{ color: "red" }}>*</span></span></th>
                                                    <th /></tr>
                                            </thead>
                                            <ReactSortable list={rows} setList={set_rows} handle=".drag-handle" tag="tbody">
                                                {rows.map((r, idx) => {
                                                    if (!row_refs.current[r.id]) row_refs.current[r.id] = React.createRef();
                                                    return (
                                                        <PlatformRow key={r.id}
                                                            ref_node={row_refs.current[r.id]}
                                                            row_state={r}
                                                            delete_row={delete_row}
                                                            update_row={update_row}
                                                            platform_options={PLATFORM_OPTIONS}
                                                        />
                                                    );
                                                })}
                                            </ReactSortable>
                                        </table>
                                    </div>

                                    <div className="edit-organisation-info" style={{ display: 'flex', marginTop: '20px', padding: '10px' }}>
                                        <button onClick={add_row} className="dark-button">Add Mapping</button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info */}
                        <div className="info-card">
                            <div className="info-header"><h4 className="info-title">Additional Info</h4></div>
                            <div className="info-content">
                                <div className="info-inner common-textarea">
                                    <label htmlFor="notes" className="form-label">Note</label>
                                    <textarea className="form-control w-100 common-textarea-all" id="notes" rows="3" name="notes" value={form_data.notes}></textarea>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
}

// PlatformRow component - portal dropdown for platform selection and naming follow Figma labels
function PlatformRow({ ref_node, row_state: r, delete_row, update_row, platform_options }) {
    const platform_button_ref = useRef(null);
    const [portal_style, set_portal_style] = useState({});

    useEffect(() => {
        if (r.platform_open && platform_button_ref.current) {
            const rect = platform_button_ref.current.getBoundingClientRect();
            set_portal_style({
                position: 'absolute',
                top: rect.bottom + window.scrollY + 8,
                left: rect.left + window.scrollX,
                width: Math.max(280, rect.width),
                maxHeight: 270,
                overflowY: 'auto',
                zIndex: 2000,
            });
        }
    }, [r.platform_open, r.platform]);
    const verifiedImage = <CircleCheck className='verified' size={16} />;
    // const verifiedImage = <img src={tickImage} alt="verified" style={{ width: 16, height: 16 }} />;

    return (
        <tr ref={ref_node} style={{ position: (r.platform_open) ? 'relative' : 'static', zIndex: (r.platform_open) ? 1000 : 'auto' }}>
            <td style={{ width: '60px' }}><button className="btn p-0" onClick={() => delete_row(r.id)} style={{ boxShadow: 'none' }}><Trash2 size={18} /></button></td>

            <td style={{ position: 'relative', minWidth: '200px', maxWidth: '200px' }}>
                <div ref={platform_button_ref} className="form-control new-form-select-cls" onClick={() => update_row(r.id, { platform_open: !r.platform_open })} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.5rem', minHeight: '40px', background: 'none' }}>
                    {/* { r.verified ? verifiedImage : ''}&nbsp;&nbsp; */}

                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginRight: 'auto' }}>{r.platform || 'Select platform'}</span>
                    <ChevronDown size={16} style={{ transform: r.platform_open ? 'rotate(180deg)' : 'rotate(0)' }} />
                </div>

                {r.platform_open && (
                    <OptionPortal style={portal_style}>
                        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                            {platform_options.map((opt, i) => (
                                <li key={i} onClick={() => update_row(r.id, { platform: opt, platform_open: false })} style={{ padding: '10px', cursor: 'pointer', borderRadius: 6, fontFamily: 'Montserrat', color: '#212529' }}>{opt}</li>
                            ))}
                        </ul>
                    </OptionPortal>
                )}

            </td>

            <td style={{ minWidth: '200px', maxWidth: '200px' }}>
                <input id={`product_id_${r.id}`} name={`product_id_${r.id}`} className="form-control common-input-all" value={r.product_id} onChange={e => update_row(r.id, { product_id: e.target.value })} />
            </td>

            <td style={{ minWidth: '200px', maxWidth: '200px' }}>
                <input id={`sku_id_${r.id}`} name={`sku_id_${r.id}`} className="form-control common-input-all" value={r.sku_id} onChange={e => update_row(r.id, { sku_id: e.target.value })} />
            </td>

            <td className="drag-handle" style={{ cursor: 'grab' }}><Menu size={18} /></td>
        </tr>
    );
}
